import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { safeParseJson } from '../utils/auth';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'best3_shop_cart_v1';

export const DEFAULT_FALLBACK_SETTINGS = {
  default_currency: 'CAD',
  supported_currencies: ['CAD', 'USD', 'EUR', 'GBP', 'AUD'],
  default_tax_name: 'HST',
  default_tax_rate: 13.0,
  flat_shipping_rate: 5.99,
  free_shipping_threshold: 50.0,
  store_notice: 'Free standard shipping on physical orders over $50 CAD!',
  paypal_enabled: true,
  paypal_mode: 'sandbox',
  paypal_merchant_name: 'Best 3 in Tennis Pro Shop',
  paypal_merchant_email: 'dimabot@gmail.com',
  paypal_support_email: 'support@best3intennis.com',
  paypal_order_notification_email: 'dimabot@gmail.com',
  paypal_allow_card_funding: true
};

export const DEFAULT_FALLBACK_TAX_ZONES = [
  {
    id: 1,
    name: 'Ontario (Default HST)',
    country_code: 'CA',
    region_code: 'ON',
    tax_name: 'HST',
    tax_rate: 13.0,
    apply_to_shipping: true,
    is_default: true,
    is_active: true
  },
  {
    id: 2,
    name: 'Atlantic Canada (NS, NB, NL, PE)',
    country_code: 'CA',
    region_code: 'NS,NB,NL,PE',
    tax_name: 'HST',
    tax_rate: 15.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 3,
    name: 'Quebec (GST + QST)',
    country_code: 'CA',
    region_code: 'QC',
    tax_name: 'GST+QST',
    tax_rate: 14.975,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 4,
    name: 'British Columbia (GST + PST)',
    country_code: 'CA',
    region_code: 'BC',
    tax_name: 'GST+PST',
    tax_rate: 12.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 5,
    name: 'Manitoba (GST + RST)',
    country_code: 'CA',
    region_code: 'MB',
    tax_name: 'GST+RST',
    tax_rate: 12.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 6,
    name: 'Saskatchewan (GST + PST)',
    country_code: 'CA',
    region_code: 'SK',
    tax_name: 'GST+PST',
    tax_rate: 11.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 7,
    name: 'Alberta & Territories (AB, NT, NU, YT)',
    country_code: 'CA',
    region_code: 'AB,NT,NU,YT',
    tax_name: 'GST',
    tax_rate: 5.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 8,
    name: 'Rest of Canada (Fallback)',
    country_code: 'CA',
    region_code: '*',
    tax_name: 'HST',
    tax_rate: 13.0,
    apply_to_shipping: true,
    is_default: false,
    is_active: true
  },
  {
    id: 9,
    name: 'United States & International',
    country_code: 'US',
    region_code: '*',
    tax_name: 'Tax',
    tax_rate: 0.0,
    apply_to_shipping: false,
    is_default: false,
    is_active: true
  }
];

export const DEFAULT_FALLBACK_PRODUCTS = [
  {
    id: 1,
    sku: 'B3T-BUNDLE-01',
    name: 'The Ultimate Player Kit (Featured Bundle)',
    slug: 'ultimate-player-kit-bundle',
    category: 'bundles',
    product_type: 'physical',
    short_description: 'Complete tournament & practice system: Mental Toughness Book + Match & Practice Logbooks + Pro Racquet Pens.',
    description:
      'Everything a competitive tennis player needs in one pro bundle:\n• 10 Tips to Improve Mental Toughness in Tennis (Physical Edition)\n• Competition Tennis Match Logbook\n• Practice & Drills Tennis Logbook\n• Pro Racquet Pens (3-Pack in Neon Yellow, Tour Green & Classic White)\n• Bonus Court Strategy Quick-Reference Card',
    price: 49.99,
    compare_at_price: 64.99,
    currency: 'CAD',
    primary_image_url: '/Images/shop/ultimate-kit.svg',
    gallery_images: [
      '/Images/shop/ultimate-kit.svg',
      '/Images/book-cover.png',
      '/Images/LogBook_cover1.png',
      '/Images/LogBook_cover2.png',
      '/Images/shop/racquet-pens.svg'
    ],
    variants: [
      {
        name: 'Pen String Color',
        options: ['Mixed 3-Pack (Recommended)', 'Neon Optic Yellow', 'Tour Court Green', 'Classic Gut White']
      }
    ],
    external_url: null,
    external_button_text: null,
    stock_quantity: 75,
    track_inventory: true,
    is_featured: true,
    is_active: true,
    sort_order: 1
  },
  {
    id: 2,
    sku: 'B3T-PEN-03',
    name: 'Pro Racquet Pens (3-Pack)',
    slug: 'pro-racquet-pens-3-pack',
    category: 'accessories',
    product_type: 'physical',
    short_description: 'Precision tennis racquet-shaped pens with textured grip and vibrant "string" color accents.',
    description:
      'Designed specifically for your tennis bag and changeover notes. Keep a pen ready beside your Match & Practice Logbooks to jot down opponent tendencies, serve patterns, and between-set adjustments.\n\nFeatures:\n• Ergonomic miniature tennis racquet barrel with overgrip texture\n• Smooth quick-dry ink that will not smudge on court\n• Available in signature string colors',
    price: 12.99,
    compare_at_price: 16.99,
    currency: 'CAD',
    primary_image_url: '/Images/shop/racquet-pens.svg',
    gallery_images: ['/Images/shop/racquet-pens.svg', '/Images/LogBook_Page1.png'],
    variants: [
      {
        name: 'String Color',
        options: ['Neon Optic Yellow', 'Tour Court Green', 'Classic Gut White', 'Mixed 3-Pack']
      }
    ],
    external_url: null,
    external_button_text: null,
    stock_quantity: 150,
    track_inventory: true,
    is_featured: false,
    is_active: true,
    sort_order: 2
  },
  {
    id: 3,
    sku: 'B3T-POSTER-01',
    name: 'Court Strategy & Mental Cues Poster',
    slug: 'court-strategy-mental-cues-poster',
    category: 'accessories',
    product_type: 'physical',
    short_description: 'High-resolution visual court geometry, percentage play zones, and between-point mental reset routines.',
    description:
      'Transform your home, office, club locker room, or training gym with our signature Court Strategy Poster. Created for competitive players and coaches, it breaks down high-percentage target zones, Wardlaw directionals, and the 20-second between-point mental routine.',
    price: 19.99,
    compare_at_price: 24.99,
    currency: 'CAD',
    primary_image_url: '/Images/shop/strategy-poster.svg',
    gallery_images: ['/Images/shop/strategy-poster.svg', '/Images/main.png'],
    variants: [
      {
        name: 'Size',
        options: ['18" x 24" Standard', '24" x 36" Club Edition']
      }
    ],
    external_url: null,
    external_button_text: null,
    stock_quantity: 90,
    track_inventory: true,
    is_featured: false,
    is_active: true,
    sort_order: 3
  },
  {
    id: 4,
    sku: 'B3T-BOOK-MT01',
    name: '10 Tips to Improve Mental Toughness in Tennis',
    slug: 'mental-toughness-in-tennis-book',
    category: 'books',
    product_type: 'physical',
    short_description: 'The definitive guide to mastering the 90% mental game: focus, confidence, pressure handling, and resilience.',
    description:
      'Tennis is 90% mental. Are you training the 90%?\n\nDivided into two actionable sections—Five Things to Focus On and Five Important Things to Remember—this book equips players of all levels with practical on-court routines to handle break points, tiebreaks, and momentum shifts.',
    price: 18.99,
    compare_at_price: 22.99,
    currency: 'CAD',
    primary_image_url: '/Images/book-cover.png',
    gallery_images: ['/Images/book-cover.png', '/Images/main.png'],
    variants: [
      {
        name: 'Format',
        options: ['Paperback (Physical)', 'Digital E-Book (PDF/EPUB)']
      }
    ],
    external_url: 'https://www.amazon.ca/dp/B0GDFC5JGP',
    external_button_text: 'Also on Amazon',
    stock_quantity: 120,
    track_inventory: true,
    is_featured: false,
    is_active: true,
    sort_order: 4
  },
  {
    id: 5,
    sku: 'B3T-LOG-COMP01',
    name: 'Competition Tennis Match Logbook',
    slug: 'competition-tennis-match-logbook',
    category: 'logbooks',
    product_type: 'physical',
    short_description: 'Structured court-side journal to chart pre-match game plans, set-by-set momentum, and post-match takeaways.',
    description:
      'Bring Tour-level preparation to every tournament match. Record opponent scouting notes, UTR/WTN ratings, court surface conditions, set scores, and what worked under pressure.',
    price: 15.99,
    compare_at_price: 19.99,
    currency: 'CAD',
    primary_image_url: '/Images/LogBook_cover1.png',
    gallery_images: [
      '/Images/LogBook_cover1.png',
      '/Images/LogBook_Page1.png',
      '/Images/LogBook_Page2.png',
      '/Images/LogBook_Page3.png'
    ],
    variants: [
      {
        name: 'Edition',
        options: ['Spiral Court Edition', 'Paperback Bound']
      }
    ],
    external_url: 'https://www.amazon.ca/dp/B0D7TT3XP4',
    external_button_text: 'Also on Amazon',
    stock_quantity: 100,
    track_inventory: true,
    is_featured: false,
    is_active: true,
    sort_order: 5
  },
  {
    id: 6,
    sku: 'B3T-LOG-PRAC01',
    name: 'Practice & Drills Tennis Logbook',
    slug: 'practice-drills-tennis-logbook',
    category: 'logbooks',
    product_type: 'physical',
    short_description: 'Dedicated training journal to log drill targets, serve percentages, fitness blocks, and coach feedback.',
    description:
      'Make every practice session count. Track your weekly training goals, technical cues from coaching lessons, serve consistency drills, and progress over the season.',
    price: 15.99,
    compare_at_price: 19.99,
    currency: 'CAD',
    primary_image_url: '/Images/LogBook_cover2.png',
    gallery_images: ['/Images/LogBook_cover2.png', '/Images/LogBook_Page3.png', '/Images/LogBook_Page4.png'],
    variants: [
      {
        name: 'Edition',
        options: ['Spiral Court Edition', 'Paperback Bound']
      }
    ],
    external_url: 'https://www.amazon.ca/dp/B0D7QPRZZ7',
    external_button_text: 'Also on Amazon',
    stock_quantity: 100,
    track_inventory: true,
    is_featured: false,
    is_active: true,
    sort_order: 6
  },
  {
    id: 7,
    sku: 'B3T-GIFT-25',
    name: 'Best3inTennis Coach & Player Gift Card',
    slug: 'coach-player-digital-gift-card',
    category: 'gift_cards',
    product_type: 'digital',
    short_description: 'Instant digital gift card for coaches to reward students or for tennis partners—redeemable across all books & gear.',
    description:
      'The perfect reward for tournament winners, team captains, or dedicated tennis students. Delivered via email with a unique redemption code and no expiration date. Zero shipping fees!',
    price: 25.0,
    compare_at_price: null,
    currency: 'CAD',
    primary_image_url: '/Images/shop/gift-card.svg',
    gallery_images: ['/Images/shop/gift-card.svg'],
    variants: [
      {
        name: 'Denomination',
        options: ['$25 CAD Gift Card', '$50 CAD Team Gift', '$100 CAD Academy Pack']
      }
    ],
    external_url: null,
    external_button_text: null,
    stock_quantity: 999,
    track_inventory: false,
    is_featured: false,
    is_active: true,
    sort_order: 7
  }
];

export const resolveTaxZoneClient = (taxZones, countryCode = 'CA', regionCode = 'ON', settings = DEFAULT_FALLBACK_SETTINGS) => {
  const country = (countryCode || 'CA').toUpperCase().trim();
  const region = (regionCode || 'ON').toUpperCase().trim();
  const zones = Array.isArray(taxZones) && taxZones.length > 0 ? taxZones : DEFAULT_FALLBACK_TAX_ZONES;

  let countryWildcard = null;
  let globalWildcard = null;
  let defaultZone = null;

  for (const zone of zones) {
    if (!zone.is_active) continue;
    if (zone.is_default && !defaultZone) {
      defaultZone = zone;
    }

    const zCountry = (zone.country_code || '').toUpperCase().trim();
    const zRegions = (zone.region_code || '*')
      .split(',')
      .map((r) => r.toUpperCase().trim());

    if (zCountry === country) {
      if (zRegions.includes(region)) {
        return zone;
      }
      if (zRegions.includes('*') && !countryWildcard) {
        countryWildcard = zone;
      }
    } else if (zCountry === '*' && !globalWildcard) {
      globalWildcard = zone;
    }
  }

  return (
    countryWildcard ||
    globalWildcard ||
    defaultZone || {
      id: 0,
      name: 'Ontario (Default HST)',
      country_code: 'CA',
      region_code: 'ON',
      tax_name: settings?.default_tax_name || 'HST',
      tax_rate: Number(settings?.default_tax_rate ?? 13.0),
      apply_to_shipping: true,
      is_default: true,
      is_active: true
    }
  );
};

export const getEffectiveUnitPrice = (product, variantSelection = '') => {
  let basePrice = Number(product?.price || 0);
  if (product?.category === 'gift_cards' && variantSelection) {
    if (variantSelection.includes('$50')) basePrice = 50.0;
    else if (variantSelection.includes('$100')) basePrice = 100.0;
    else if (variantSelection.includes('$25')) basePrice = 25.0;
  }
  return basePrice;
};

export const isDiscountRuleCurrentlyValid = (d) => {
  if (!d || d.is_active === false) return false;
  if (d.max_uses !== null && d.max_uses !== undefined && Number(d.max_uses) > 0) {
    if (Number(d.used_count || 0) >= Number(d.max_uses)) return false;
  }
  const noTimeLimit = Boolean(d.no_time_limit) || d.discount_type === 'free_shipping_product';
  if (!noTimeLimit) {
    const now = Date.now();
    if (d.starts_at) {
      const startMs = new Date(d.starts_at).getTime();
      if (!Number.isNaN(startMs) && startMs > now) return false;
    }
    if (d.ends_at) {
      const endMs = new Date(d.ends_at).getTime();
      if (!Number.isNaN(endMs) && endMs < now) return false;
    }
  }
  return true;
};

export const ruleAppliesToProductId = (rule, productId) => {
  if (!rule) return false;
  if (rule.applies_to === 'all' || rule.applies_to === 'all_products') return true;
  const pids = Array.isArray(rule.product_ids)
    ? rule.product_ids.map(Number)
    : Array.isArray(rule.product_ids_json)
    ? rule.product_ids_json.map(Number)
    : [];
  return pids.includes(Number(productId));
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (_e) {}
    return [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [products, setProducts] = useState(() => {
    try {
      const localProds = localStorage.getItem('best3_local_admin_products');
      if (localProds) {
        const parsed = JSON.parse(localProds);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_e) {}
    return DEFAULT_FALLBACK_PRODUCTS;
  });
  const [settings, setSettings] = useState(() => {
    try {
      const localSettings = localStorage.getItem('best3_local_admin_settings');
      if (localSettings) {
        const parsed = JSON.parse(localSettings);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_FALLBACK_SETTINGS, ...parsed };
      }
    } catch (_e) {}
    return DEFAULT_FALLBACK_SETTINGS;
  });
  const [taxZones, setTaxZones] = useState(() => {
    try {
      const localZones = localStorage.getItem('best3_local_admin_tax_zones');
      if (localZones) {
        const parsed = JSON.parse(localZones);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_e) {}
    return DEFAULT_FALLBACK_TAX_ZONES;
  });
  const [discounts, setDiscounts] = useState(() => {
    try {
      const localDiscounts = localStorage.getItem('best3_local_admin_discounts');
      if (localDiscounts) {
        const parsed = JSON.parse(localDiscounts);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (_e) {}
    return [];
  });

  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoRule, setAppliedPromoRule] = useState(null);
  const [promoFeedback, setPromoFeedback] = useState(null);

  const [paypalConfig, setPaypalConfig] = useState({
    client_id: 'sb',
    mode: 'sandbox',
    currency: 'CAD'
  });
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState('CA');
  const [selectedRegion, setSelectedRegion] = useState('ON');

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const res = await fetch('/best3intennis-api/api/get_products.php');
      if (res.ok) {
        const text = await res.text();
        const data = safeParseJson(text);
        if (data && data.success) {
          if (Array.isArray(data.products) && data.products.length > 0) {
            setProducts(data.products);
          }
          if (data.settings) {
            setSettings((prev) => ({ ...prev, ...data.settings }));
          }
          if (Array.isArray(data.tax_zones) && data.tax_zones.length > 0) {
            setTaxZones(data.tax_zones);
          }
          if (Array.isArray(data.discounts)) {
            setDiscounts(data.discounts);
            localStorage.setItem('best3_local_admin_discounts', JSON.stringify(data.discounts));
          }
          if (data.paypal) {
            setPaypalConfig((prev) => ({ ...prev, ...data.paypal }));
          }
        }
      }
    } catch (_err) {
      // Keep default fallback catalog & CAD/HST 13% settings (or local admin overrides)
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (_e) {}
  }, [items]);

  const addToCart = useCallback(
    (product, quantity = 1, variant = '', openDrawer = true) => {
      if (!product) return;

      const defaultVariant =
        variant ||
        (Array.isArray(product.variants) &&
        product.variants.length > 0 &&
        Array.isArray(product.variants[0].options) &&
        product.variants[0].options.length > 0
          ? product.variants[0].options[0]
          : '');

      const cartItemId = `${product.id}::${defaultVariant}`;
      const unitPrice = getEffectiveUnitPrice(product, defaultVariant);
      let itemType = product.product_type || 'physical';
      if (
        defaultVariant.toLowerCase().includes('digital') ||
        defaultVariant.toLowerCase().includes('e-book')
      ) {
        itemType = 'digital';
      }

      setItems((prev) => {
        const existingIdx = prev.findIndex((item) => item.cartItemId === cartItemId);
        if (existingIdx > -1) {
          const updated = [...prev];
          const currentQty = updated[existingIdx].quantity;
          const maxQty = product.track_inventory ? Math.max(1, product.stock_quantity || 99) : 99;
          updated[existingIdx] = {
            ...updated[existingIdx],
            quantity: Math.min(maxQty, currentQty + quantity),
            price: unitPrice,
            freeShipping: Boolean(product.free_shipping)
          };
          return updated;
        }

        return [
          ...prev,
          {
            cartItemId,
            productId: product.id,
            sku: product.sku || '',
            name: product.name,
            price: unitPrice,
            currency: product.currency || settings.default_currency || 'CAD',
            image: product.primary_image_url,
            variant: defaultVariant,
            quantity: Math.max(1, quantity),
            productType: itemType,
            freeShipping: Boolean(product.free_shipping),
            maxStock: product.track_inventory ? Math.max(1, product.stock_quantity || 99) : 99
          }
        ];
      });

      if (openDrawer) {
        setIsCartOpen(true);
      }
    },
    [settings.default_currency]
  );

  const updateQuantity = useCallback((cartItemId, newQuantity) => {
    const qty = Number(newQuantity);
    if (qty <= 0) {
      setItems((prev) => prev.filter((item) => item.cartItemId !== cartItemId));
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: Math.min(item.maxStock || 99, qty) }
          : item
      )
    );
  }, []);

  const removeFromCart = useCallback((cartItemId) => {
    setItems((prev) => prev.filter((item) => item.cartItemId !== cartItemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setAppliedPromoRule(null);
    setPromoCodeInput('');
    setPromoFeedback(null);
  }, []);

  const cartCount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      Number(
        items.reduce((sum, item) => sum + Number(item.price) * Number(item.quantity), 0).toFixed(2)
      ),
    [items]
  );

  const requiresShipping = useMemo(
    () => items.some((item) => item.productType === 'physical'),
    [items]
  );

  const validDiscounts = useMemo(
    () => (Array.isArray(discounts) ? discounts.filter(isDiscountRuleCurrentlyValid) : []),
    [discounts]
  );

  const getProductPromotions = useCallback(
    (product) => {
      if (!product) {
        return {
          hasFreeShipping: false,
          percentageOff: 0,
          fixedOff: 0,
          badges: []
        };
      }
      const pid = Number(product.id);
      let hasFreeShipping = Boolean(product.free_shipping);
      let percentageOff = 0;
      let fixedOff = 0;
      const badges = [];

      for (const d of validDiscounts) {
        if (!ruleAppliesToProductId(d, pid)) continue;
        if (d.discount_type === 'free_shipping_product') {
          hasFreeShipping = true;
        } else if (d.trigger_type === 'automatic') {
          if (d.discount_type === 'percentage') {
            percentageOff = Math.max(percentageOff, Number(d.discount_value || 0));
          } else if (d.discount_type === 'fixed_amount') {
            fixedOff = Math.max(fixedOff, Number(d.discount_value || 0));
          }
        }
      }

      if (percentageOff > 0) {
        badges.push(`${percentageOff}% OFF`);
      }
      if (fixedOff > 0) {
        badges.push(`-$${fixedOff.toFixed(2)} OFF`);
      }
      if (hasFreeShipping && product.product_type !== 'digital') {
        badges.push('🚚 Free Shipping');
      }

      return {
        hasFreeShipping,
        percentageOff,
        fixedOff,
        badges
      };
    },
    [validDiscounts]
  );

  const applyPromoCode = useCallback(
    async (rawCode) => {
      const code = String(rawCode ?? promoCodeInput).trim().toUpperCase();
      if (!code) {
        setPromoFeedback({ type: 'error', text: 'Please enter a promo or discount code.' });
        return false;
      }

      // 1. Try server validation first
      let matchedRule = null;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        const res = await fetch(
          `/best3intennis-api/api/admin_discounts.php?action=validate_code&code=${encodeURIComponent(
            code
          )}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        const data = safeParseJson(await res.text());
        if (res.ok && data && data.success && data.discount) {
          matchedRule = data.discount;
        } else if (data && data.message && res.status >= 400 && res.status < 500 && res.status !== 404) {
          setPromoFeedback({ type: 'error', message: data.message, text: data.message });
          return false;
        }
      } catch (_e) {
        // Fallback to local discount rules
      }

      // 2. Fallback to local discounts state / localStorage
      if (!matchedRule) {
        const localSaved = safeParseJson(localStorage.getItem('best3_local_admin_discounts'));
        const pool = Array.isArray(localSaved) && localSaved.length > 0 ? localSaved : discounts;
        const candidate = (pool || []).find(
          (d) => d && d.code && String(d.code).toUpperCase() === code
        );
        if (!candidate) {
          const msg = `Promo code "${code}" was not found.`;
          setPromoFeedback({ type: 'error', message: msg, text: msg });
          return false;
        }
        if (candidate.is_active === false) {
          const msg = `Promo code "${code}" is currently inactive.`;
          setPromoFeedback({ type: 'error', message: msg, text: msg });
          return false;
        }
        if (
          candidate.max_uses !== null &&
          candidate.max_uses !== undefined &&
          Number(candidate.max_uses) > 0 &&
          Number(candidate.used_count || 0) >= Number(candidate.max_uses)
        ) {
          const msg = `Promo code "${code}" has reached its maximum usage limit (${candidate.max_uses} uses).`;
          setPromoFeedback({ type: 'error', message: msg, text: msg });
          return false;
        }
        if (!isDiscountRuleCurrentlyValid(candidate)) {
          const msg = `Promo code "${code}" is outside its active date range.`;
          setPromoFeedback({ type: 'error', message: msg, text: msg });
          return false;
        }
        matchedRule = candidate;
      }

      // Check product eligibility if cart is non-empty
      if (items.length > 0 && matchedRule.applies_to === 'selected_products') {
        const hasEligible = items.some((it) => ruleAppliesToProductId(matchedRule, it.productId));
        if (!hasEligible) {
          const msg = `Promo code "${code}" applies only to specific selected products not currently in your cart.`;
          setPromoFeedback({ type: 'error', message: msg, text: msg });
          return false;
        }
      }

      setAppliedPromoRule(matchedRule);
      setPromoCodeInput(code);
      const desc =
        matchedRule.discount_type === 'free_shipping_code'
          ? `Free Shipping code "${code}" applied!`
          : matchedRule.discount_type === 'percentage'
          ? `Code "${code}" applied (${matchedRule.discount_value}% OFF)!`
          : `Code "${code}" applied (-$${Number(matchedRule.discount_value).toFixed(2)} OFF)!`;
      setPromoFeedback({ type: 'success', message: desc, text: desc });
      return true;
    },
    [promoCodeInput, discounts, items]
  );

  const removePromoCode = useCallback(() => {
    setAppliedPromoRule(null);
    setPromoCodeInput('');
    setPromoFeedback(null);
  }, []);

  const recordLocalDiscountRedemptions = useCallback((consumedIds) => {
    if (!Array.isArray(consumedIds) || consumedIds.length === 0) return;
    const idSet = new Set(consumedIds.map(Number));
    setDiscounts((prev) => {
      const next = prev.map((d) =>
        idSet.has(Number(d.id))
          ? { ...d, used_count: Number(d.used_count || 0) + 1 }
          : d
      );
      try {
        localStorage.setItem('best3_local_admin_discounts', JSON.stringify(next));
      } catch (_e) {}
      return next;
    });
  }, []);

  // Evaluate Cart Discounts & Free Shipping Rules
  const discountEvaluation = useMemo(() => {
    if (items.length === 0) {
      return {
        discountAmount: 0.0,
        discountedSubtotal: 0.0,
        freeShippingUnlocked: false,
        freeShippingReason: null,
        discountSummary: null,
        consumedDiscountIds: []
      };
    }

    let autoSavings = 0.0;
    const appliedLabels = [];
    const consumedIds = new Set();
    let freeShippingUnlocked = false;
    let freeShippingReason = null;

    // 1. Check product-level permanent free shipping (without time limit)
    for (const item of items) {
      const prodObj = products.find((p) => Number(p.id) === Number(item.productId));
      if (item.freeShipping || (prodObj && prodObj.free_shipping)) {
        freeShippingUnlocked = true;
        freeShippingReason = `Free Shipping (${item.name})`;
      }
      for (const d of validDiscounts) {
        if (d.discount_type === 'free_shipping_product' && ruleAppliesToProductId(d, item.productId)) {
          freeShippingUnlocked = true;
          freeShippingReason = d.title || d.name || 'Free Shipping';
          consumedIds.add(Number(d.id));
        }
      }
    }

    // 2. Check automatic percentage or fixed_amount discounts
    for (const d of validDiscounts) {
      if (d.trigger_type !== 'automatic') continue;
      if (d.discount_type !== 'percentage' && d.discount_type !== 'fixed_amount') continue;
      if (subtotal < Number(d.min_order_amount || 0)) continue;

      let eligibleSubtotal = 0.0;
      let eligibleQty = 0;
      for (const item of items) {
        if (ruleAppliesToProductId(d, item.productId)) {
          eligibleSubtotal += Number(item.price) * Number(item.quantity);
          eligibleQty += Number(item.quantity);
        }
      }
      if (eligibleSubtotal <= 0) continue;

      let ruleSavings = 0.0;
      if (d.discount_type === 'percentage') {
        const pct = Math.min(100, Math.max(0, Number(d.discount_value || 0)));
        ruleSavings = Number(((eligibleSubtotal * pct) / 100.0).toFixed(2));
      } else if (d.discount_type === 'fixed_amount') {
        const fixedVal = Math.max(0, Number(d.discount_value || 0));
        ruleSavings =
          d.applies_to === 'selected_products'
            ? Math.min(eligibleSubtotal, Number((fixedVal * Math.max(1, eligibleQty)).toFixed(2)))
            : Math.min(eligibleSubtotal, Number(fixedVal.toFixed(2)));
      }

      if (ruleSavings > 0) {
        autoSavings = Number((autoSavings + ruleSavings).toFixed(2));
        appliedLabels.push(d.title || d.name || 'Promotion');
        consumedIds.add(Number(d.id));
      }
    }

    // 3. Check applied Promo Code rule
    let codeSavings = 0.0;
    if (appliedPromoRule && isDiscountRuleCurrentlyValid(appliedPromoRule)) {
      const d = appliedPromoRule;
      if (subtotal >= Number(d.min_order_amount || 0)) {
        const promoLabel = d.title || d.name || 'Discount';
        if (d.discount_type === 'free_shipping_code') {
          freeShippingUnlocked = true;
          freeShippingReason = `Code ${d.code} (${promoLabel})`;
          appliedLabels.push(`Code ${d.code}: Free Shipping`);
          consumedIds.add(Number(d.id));
        } else if (d.discount_type === 'percentage' || d.discount_type === 'fixed_amount') {
          let eligibleSubtotal = 0.0;
          let eligibleQty = 0;
          for (const item of items) {
            if (ruleAppliesToProductId(d, item.productId)) {
              eligibleSubtotal += Number(item.price) * Number(item.quantity);
              eligibleQty += Number(item.quantity);
            }
          }
          if (eligibleSubtotal > 0) {
            if (d.discount_type === 'percentage') {
              const pct = Math.min(100, Math.max(0, Number(d.discount_value || 0)));
              codeSavings = Number(((eligibleSubtotal * pct) / 100.0).toFixed(2));
            } else {
              const fixedVal = Math.max(0, Number(d.discount_value || 0));
              codeSavings =
                d.applies_to === 'selected_products'
                  ? Math.min(eligibleSubtotal, Number((fixedVal * Math.max(1, eligibleQty)).toFixed(2)))
                  : Math.min(eligibleSubtotal, Number(fixedVal.toFixed(2)));
            }
            if (!consumedIds.has(Number(d.id)) && codeSavings > 0) {
              appliedLabels.push(`Code ${d.code} (${promoLabel})`);
              consumedIds.add(Number(d.id));
            } else {
              codeSavings = 0.0;
            }
          }
        }
      }
    }

    const totalDiscount = Math.min(subtotal, Number((autoSavings + codeSavings).toFixed(2)));
    const discountedSubtotal = Math.max(0.0, Number((subtotal - totalDiscount).toFixed(2)));
    if (freeShippingUnlocked && freeShippingReason && !appliedLabels.includes(freeShippingReason)) {
      appliedLabels.push(freeShippingReason);
    }

    return {
      discountAmount: totalDiscount,
      discountedSubtotal,
      freeShippingUnlocked,
      freeShippingReason,
      discountSummary: appliedLabels.length > 0 ? Array.from(new Set(appliedLabels)).join(' + ') : null,
      consumedDiscountIds: Array.from(consumedIds)
    };
  }, [items, products, validDiscounts, subtotal, appliedPromoRule]);

  const discountAmount = discountEvaluation.discountAmount;
  const discountedSubtotal = discountEvaluation.discountedSubtotal;
  const freeShippingUnlocked = discountEvaluation.freeShippingUnlocked;
  const freeShippingReason = discountEvaluation.freeShippingReason;
  const discountSummary = discountEvaluation.discountSummary;
  const consumedDiscountIds = discountEvaluation.consumedDiscountIds;

  const flatShippingRate = Number(settings.flat_shipping_rate ?? 5.99);
  const freeShippingThreshold = Number(settings.free_shipping_threshold ?? 50.0);

  const shippingAmount = useMemo(() => {
    if (items.length === 0 || !requiresShipping) return 0.0;
    if (freeShippingUnlocked) return 0.0;
    if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) return 0.0;
    return Number(flatShippingRate.toFixed(2));
  }, [
    items.length,
    requiresShipping,
    freeShippingUnlocked,
    freeShippingThreshold,
    subtotal,
    flatShippingRate
  ]);

  const freeShippingRemaining = useMemo(() => {
    if (!requiresShipping || freeShippingUnlocked || freeShippingThreshold <= 0) return 0;
    return Math.max(0, Number((freeShippingThreshold - subtotal).toFixed(2)));
  }, [requiresShipping, freeShippingUnlocked, freeShippingThreshold, subtotal]);

  const activeTaxZone = useMemo(
    () => resolveTaxZoneClient(taxZones, selectedCountry, selectedRegion, settings),
    [taxZones, selectedCountry, selectedRegion, settings]
  );

  const taxAmount = useMemo(() => {
    if (items.length === 0) return 0.0;
    const rate = Number(activeTaxZone?.tax_rate ?? 13.0);
    const applyShip = activeTaxZone?.apply_to_shipping !== false;
    const taxableBase = discountedSubtotal + (applyShip ? shippingAmount : 0.0);
    return Number(((taxableBase * rate) / 100.0).toFixed(2));
  }, [items.length, discountedSubtotal, shippingAmount, activeTaxZone]);

  const totalAmount = useMemo(
    () => Number((discountedSubtotal + shippingAmount + taxAmount).toFixed(2)),
    [discountedSubtotal, shippingAmount, taxAmount]
  );

  const currency = settings.default_currency || 'CAD';

  const formatPrice = useCallback(
    (amount, currOverride) => {
      const curr = currOverride || currency || 'CAD';
      const num = Number(amount || 0);
      return `$${num.toFixed(2)} ${curr}`;
    },
    [currency]
  );

  const value = useMemo(
    () => ({
      items,
      isCartOpen,
      setIsCartOpen,
      products,
      setProducts,
      settings,
      setSettings,
      taxZones,
      setTaxZones,
      discounts,
      setDiscounts,
      validDiscounts,
      getProductPromotions,
      promoCodeInput,
      setPromoCodeInput,
      appliedPromoRule,
      promoFeedback,
      applyPromoCode,
      removePromoCode,
      recordLocalDiscountRedemptions,
      paypalConfig,
      catalogLoading,
      fetchCatalog,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      cartCount,
      subtotal,
      discountAmount,
      discountedSubtotal,
      discountSummary,
      consumedDiscountIds,
      requiresShipping,
      shippingAmount,
      freeShippingUnlocked,
      freeShippingReason,
      freeShippingThreshold,
      freeShippingRemaining,
      selectedCountry,
      setSelectedCountry,
      selectedRegion,
      setSelectedRegion,
      activeTaxZone,
      taxAmount,
      totalAmount,
      currency,
      formatPrice
    }),
    [
      items,
      isCartOpen,
      products,
      settings,
      taxZones,
      discounts,
      validDiscounts,
      getProductPromotions,
      promoCodeInput,
      appliedPromoRule,
      promoFeedback,
      applyPromoCode,
      removePromoCode,
      recordLocalDiscountRedemptions,
      paypalConfig,
      catalogLoading,
      fetchCatalog,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      cartCount,
      subtotal,
      discountAmount,
      discountedSubtotal,
      discountSummary,
      consumedDiscountIds,
      requiresShipping,
      shippingAmount,
      freeShippingUnlocked,
      freeShippingReason,
      freeShippingThreshold,
      freeShippingRemaining,
      selectedCountry,
      selectedRegion,
      activeTaxZone,
      taxAmount,
      totalAmount,
      currency,
      formatPrice
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
};
